import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  DialogTitle,
  Stack,
  Slide,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  BiError, 
  BiCheckCircle, 
  BiInfoCircle, 
  BiX 
} from 'react-icons/bi';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '15px',
    minWidth: '280px',
    maxWidth: '600px',
    width: '90vw',
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
      minWidth: '400px',
    },
  },
}));

const AutobiaConfirmationModal = ({
  open = false,
  onClose = () => {},
  title = 'Confirmation',
  message = '',
  type = 'info',
  variant = 'default',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm = () => {},
  loading = false,
  children,
  showCloseButton = true,
  slideDirection = 'left',
  ...props
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const getTypeConfig = () => {
    switch (type) {
      case 'error':
      case 'danger':
        return {
          icon: BiError,
          color: 'error',
          bgColor: 'error.light',
          textColor: 'error.main',
        };
      case 'success':
        return {
          icon: BiCheckCircle,
          color: 'success',
          bgColor: 'success.light',
          textColor: 'success.main',
        };
      case 'warning':
        return {
          icon: BiError,
          color: 'warning',
          bgColor: 'warning.light',
          textColor: 'warning.main',
        };
      default:
        return {
          icon: BiInfoCircle,
          color: 'primary',
          bgColor: 'primary.light',
          textColor: 'primary.main',
        };
    }
  };

  const typeConfig = getTypeConfig();
  const IconComponent = typeConfig.icon;

  const handleModalClose = () => {
    if (loading) return;
    
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleConfirm = () => {
    if (loading) return;
    onConfirm();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          dialogTitle: {
            backgroundColor: 'transparent',
            borderBottom: `1px solid ${(theme) => theme.palette.divider}`,
          },
          showIcon: false,
        };
      case 'elevated':
        return {
          dialogTitle: {
            background: `linear-gradient(135deg, ${typeConfig.bgColor} 0%, ${typeConfig.textColor}20 100%)`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
          showIcon: true,
        };
      default:
        return {
          dialogTitle: {
            backgroundColor: typeConfig.bgColor,
          },
          showIcon: true,
        };
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <StyledDialog
      open={open && !isClosing}
      onClose={handleModalClose}
      TransitionComponent={Slide}
      TransitionProps={{ 
        direction: slideDirection, 
        timeout: variant === 'minimal' ? 300 : 700 
      }}
      {...props}
    >
      <DialogTitle sx={variantStyles.dialogTitle}>
        <Stack 
          direction="row" 
          alignItems="center" 
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap="0.5rem">
            {variantStyles.showIcon && (
              <IconComponent 
                fontSize="1.5rem" 
                color={typeConfig.textColor}
              />
            )}
            <Typography
              variant="h6"
              fontWeight={600}
              color={typeConfig.textColor}
            >
              {title}
            </Typography>
          </Stack>
          {showCloseButton && (
            <IconButton 
              onClick={handleModalClose}
              size="small"
              disabled={loading}
              sx={{ color: typeConfig.textColor }}
            >
              <BiX />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: '1.5rem' }}>
        <Stack direction="column" gap="1rem">
          {message && (
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
          )}
          {children}
        </Stack>
      </DialogContent>

      {variant !== 'minimal' && <Divider />}

      <DialogActions sx={{ padding: '1rem 1.5rem' }}>
        <Stack direction="row" gap="0.5rem" width="100%" justifyContent="flex-end">
          {!loading && (
            <Button
              color={typeConfig.color}
              onClick={handleModalClose}
              sx={{ 
                borderRadius: '8px', 
                height: '40px',
                minWidth: '80px',
              }}
            >
              {cancelText}
            </Button>
          )}
          <Button
            variant="contained"
            color={typeConfig.color}
            onClick={handleConfirm}
            disabled={loading}
            sx={{ 
              borderRadius: '8px', 
              height: '40px',
              minWidth: '100px',
              position: 'relative',
            }}
          >
            {loading ? (
              <Box
                sx={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid currentColor',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            ) : (
              confirmText
            )}
          </Button>
        </Stack>
      </DialogActions>
    </StyledDialog>
  );
};

export default AutobiaConfirmationModal;